import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, ChevronDown, ChevronUp, Star } from 'lucide-react';
import { FoundingOfferCountdown } from '../../components/si/FoundingOfferCountdown';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }),
};

const FREE_FEATURES = [
  'Access to the Session Intelligence™ dashboard',
  'One free session analysis',
  'Audio, transcript, or notes upload',
  'Structured Session Intelligence™ report',
  'Coaching hypotheses for the coach to validate',
  'Live walkthrough access',
  'No credit card required',
];

const PRO_FEATURES = [
  'Unlimited Session Intelligence™ reports',
  'Uses your own connected AI usage account',
  'Audio, transcript, or notes upload',
  'Structured 14-section insight report',
  'AI-generated session summary',
  'Limiting belief detection',
  'Emotional language signals',
  'Contradictions and parts conflicts',
  'NLP Meta Model patterns',
  'Identity-level statements',
  'Suggested next-session questions',
  'Coaching hypotheses for the coach to validate',
  'Client dashboard',
  'Client/session history',
  'Coach Intelligence AI',
  'Pattern History',
  'Next Session Planner',
  'Follow-Up Assistant',
  'Question Bank',
  'Consent & Ethics Centre',
  'Better support for recurring client work',
  'Use reports to prepare more effectively for ongoing sessions',
];

const FULL_EXTRAS = [
  'Custom Lens™',
  'Analyse sessions through your own coaching or practitioner framework',
  'Create your own analysis approach',
  'Choose what the AI looks for',
  'Choose what the AI avoids',
  'Customise report focus and question style',
  'Best for multi-modality practitioners',
  'Ideal for counsellors, hypnotherapists, NLP practitioners, somatic coaches, leadership coaches, consultants, and specialist practitioners',
  'Priority access to new features',
  'Setup support included',
];

const FAQS = [
  {
    q: 'What does the Free Starter plan include?',
    a: 'The Free Starter plan gives you access to the Session Intelligence™ dashboard and one free session analysis. No credit card is required. After using your free analysis, you can upgrade to continue using Session Intelligence™ with your clients.',
  },
  {
    q: 'How does the connected AI usage account work?',
    a: 'Session Intelligence™ uses your own AI usage account so we can keep the monthly platform price lower and allow unlimited report generation. AI usage is billed separately by the AI provider and depends on session length and volume. We help you set this up during onboarding — no technical experience required.',
  },
  {
    q: 'What is the $199 AI Setup & Onboarding Fee?',
    a: 'This is a one-time fee that covers helping you connect your AI usage account, configuring your Session Intelligence™ workspace, and showing you how to generate your first session report. If you join on a yearly plan during early access, this fee is waived.',
  },
  {
    q: 'Can I upload audio?',
    a: 'Yes. You can upload coaching session audio (MP3, M4A, WAV, OGG, WebM — up to 200 MB), transcripts, or session notes.',
  },
  {
    q: 'Do I need client permission before uploading?',
    a: 'Yes. You should only upload session material that you have permission to record, transcribe, and use for AI-assisted coaching support. Session Intelligence™ includes a consent reminder in the upload flow.',
  },
  {
    q: 'Is this therapy or diagnosis?',
    a: 'No. Session Intelligence™ is not a therapy, diagnosis, or mental health assessment tool. It provides coaching hypotheses and suggested areas for the coach to explore and validate with the client.',
  },
  {
    q: 'What is Custom Lens™?',
    a: 'Custom Lens™ is available on the Full Access plan and allows you to guide Session Intelligence™ to analyse session language and client patterns through your preferred coaching or practitioner framework — while keeping the core safety rules in place.',
  },
  {
    q: 'Do I need technical experience?',
    a: 'No. We help you set everything up during onboarding.',
  },
];

type BillingCycle = 'monthly' | 'yearly';

function FeatureItem({ text, dark = false }: { text: string; dark?: boolean }) {
  return (
    <li className="flex items-start gap-2.5">
      <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${dark ? 'text-success-40' : 'text-success-50'}`} />
      <span className={`text-sm leading-relaxed ${dark ? 'text-gray-30' : 'text-gray-60'}`}>{text}</span>
    </li>
  );
}

export function SIPricingPage() {
  const [billing, setBilling] = useState<BillingCycle>('monthly');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const monthly = billing === 'monthly';

  return (
    <div className="min-h-screen bg-white font-urbanist text-gray-80">

      {/* NAV */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-gray-20">
        <div className="section-container flex items-center justify-between h-16">
          <Link to="/" className="text-sm font-semibold text-gray-80 hover:text-gray-60 transition-colors">
            Session Intelligence<sup className="text-[65%] align-super">™</sup>
          </Link>
          <div className="flex items-center gap-5">
            <Link to="/si/pricing" className="text-sm font-medium text-success-50">Pricing</Link>
            <Link to="/si/login" className="text-sm font-medium text-gray-60 hover:text-gray-80 transition-colors">
              Sign In
            </Link>
          </div>
        </div>
      </nav>

      {/* FOUNDING OFFER BANNER */}
      <FoundingOfferCountdown />

      {/* HERO */}
      <section className="pt-20 pb-16 bg-gradient-to-b from-brand-5 to-white">
        <div className="section-container max-w-3xl text-center">
          <motion.h1
            variants={fadeUp} initial="hidden" animate="visible" custom={0}
            className="text-4xl md:text-5xl font-bold text-gray-80 leading-tight mb-5"
          >
            Simple pricing for Session Intelligence<sup className="text-[55%] align-super">™</sup>
          </motion.h1>
          <motion.p
            variants={fadeUp} initial="hidden" animate="visible" custom={1}
            className="text-lg text-gray-50 leading-relaxed mb-6 max-w-2xl mx-auto"
          >
            Start free and try Session Intelligence<sup className="text-[65%] align-super">™</sup> with one real session. Upgrade when you're ready to use it with all your clients.
          </motion.p>

          {/* Billing toggle — hidden on free, shown for paid plans */}
          <motion.div
            variants={fadeUp} initial="hidden" animate="visible" custom={2}
            className="inline-flex items-center gap-1 bg-gray-10 rounded-full p-1 mb-3"
          >
            <button
              onClick={() => setBilling('monthly')}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                monthly ? 'bg-white text-gray-80 shadow-sm' : 'text-gray-50 hover:text-gray-70'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBilling('yearly')}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 flex items-center gap-2 ${
                !monthly ? 'bg-white text-gray-80 shadow-sm' : 'text-gray-50 hover:text-gray-70'
              }`}
            >
              Yearly
              <span className="text-xs font-bold text-success-50 bg-success-10 px-2 py-0.5 rounded-full">
                2 Months Free
              </span>
            </button>
          </motion.div>

          <motion.p
            variants={fadeUp} initial="hidden" animate="visible" custom={3}
            className="text-xs text-gray-40 max-w-xl mx-auto leading-relaxed"
          >
            Paid plans include unlimited Session Intelligence<sup className="text-[65%] align-super">™</sup> reports using your own connected AI usage account. We help you set this up during onboarding.
          </motion.p>
        </div>
      </section>

      {/* PRICING CARDS */}
      <section className="pb-24">
        <div className="section-container">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">

            {/* FREE STARTER */}
            <motion.div
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0}
              className="bg-white rounded-3xl border border-gray-20 p-7 flex flex-col"
            >
              <div className="mb-6">
                <p className="text-xs font-bold uppercase tracking-widest text-gray-40 mb-3">Free Starter</p>
                <div className="flex items-end gap-1 mb-1">
                  <span className="text-4xl font-bold text-gray-80">$0</span>
                </div>
                <p className="text-xs text-gray-40 mb-3">No credit card required</p>
                <p className="text-sm text-gray-50 leading-relaxed">
                  For coaches and practitioners who want to try Session Intelligence<sup className="text-[65%] align-super">™</sup> before upgrading.
                </p>
              </div>
              <ul className="space-y-2.5 mb-8 flex-1">
                {FREE_FEATURES.map(f => <FeatureItem key={f} text={f} />)}
              </ul>
              <Link
                to="/si/register"
                className="w-full text-center btn-secondary py-3 text-sm font-semibold"
              >
                Get Free Account
              </Link>
              <p className="text-xs text-gray-40 leading-relaxed mt-4">
                Your free account includes one free session analysis. Upgrade to continue using Session Intelligence<sup className="text-[65%] align-super">™</sup> with your clients.
              </p>
            </motion.div>

            {/* PROFESSIONAL */}
            <motion.div
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1}
              className="bg-gray-80 rounded-3xl p-7 flex flex-col relative overflow-hidden"
            >
              <div className="absolute top-5 right-5">
                <span className="text-xs font-bold bg-success-40 text-white px-3 py-1 rounded-full">Most Popular</span>
              </div>
              <div className="mb-6">
                <p className="text-xs font-bold uppercase tracking-widest text-gray-40 mb-3">Professional</p>
                <div className="flex items-end gap-1 mb-1">
                  <span className="text-4xl font-bold text-white">{monthly ? '$49' : '$490'}</span>
                  <span className="text-gray-40 text-sm mb-1.5">/{monthly ? 'month' : 'year'}</span>
                </div>
                {!monthly && (
                  <p className="text-xs text-success-40 font-semibold mb-3">2 months free</p>
                )}
                <p className="text-sm text-gray-30 leading-relaxed">
                  For coaches, practitioners, counsellors, hypnotherapists, NLP professionals, consultants, and leadership coaches who want to use Session Intelligence<sup className="text-[65%] align-super">™</sup> as part of their regular client work.
                </p>
              </div>
              <ul className="space-y-2.5 mb-8 flex-1">
                {PRO_FEATURES.map(f => (
                  <li key={f} className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 text-success-40 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-30 leading-relaxed">{f}</span>
                  </li>
                ))}
              </ul>
              <a
                href={monthly
                  ? 'https://buy.stripe.com/28E8wP9FB4YK97vekP43S0r'
                  : 'https://buy.stripe.com/fZueVd6tpgHs2J71y343S0p'}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full text-center btn-primary py-3 text-sm font-semibold"
              >
                Choose Professional
              </a>
              <p className="text-xs text-gray-40 leading-relaxed mt-4">
                {monthly && 'Includes a one-time $199 AI Setup & Onboarding Fee charged at checkout. '}AI usage is billed separately through your own connected AI usage account. We help you set this up during onboarding.
              </p>
            </motion.div>

            {/* FULL ACCESS */}
            <motion.div
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2}
              className="bg-white rounded-3xl border border-gray-20 p-7 flex flex-col"
            >
              <div className="mb-6">
                <p className="text-xs font-bold uppercase tracking-widest text-gray-40 mb-3">Full Access</p>
                <div className="flex items-end gap-1 mb-1">
                  <span className="text-4xl font-bold text-gray-80">{monthly ? '$79' : '$790'}</span>
                  <span className="text-gray-40 text-sm mb-1.5">/{monthly ? 'month' : 'year'}</span>
                </div>
                {!monthly && (
                  <p className="text-xs text-success-50 font-semibold mb-3">2 months free · $199 setup fee waived during early access</p>
                )}
                <p className="text-sm text-gray-50 leading-relaxed">
                  For coaches and practitioners who want the most complete Session Intelligence<sup className="text-[65%] align-super">™</sup> experience, including the ability to analyse sessions through their own method or modality.
                </p>
              </div>
              <div className="mb-3 flex-1">
                <p className="text-xs font-semibold text-gray-40 uppercase tracking-wide mb-2.5">Everything in Professional, plus:</p>
                <ul className="space-y-2.5">
                  {FULL_EXTRAS.map(f => <FeatureItem key={f} text={f} />)}
                </ul>
              </div>
              <a
                href={monthly
                  ? 'https://buy.stripe.com/14A00j2d9crc1F32C743S0s'
                  : 'https://buy.stripe.com/bJe7sL1952QC6ZnccH43S0q'}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full text-center btn-secondary py-3 text-sm font-semibold mt-6"
              >
                Choose Full Access
              </a>
              <p className="text-xs text-gray-40 leading-relaxed mt-4">
                Custom Lens<sup className="text-[65%] align-super">™</sup> allows you to guide Session Intelligence<sup className="text-[65%] align-super">™</sup> to analyse client language and session patterns through your preferred approach while keeping the core safety rules in place.
              </p>
            </motion.div>

          </div>
        </div>
      </section>

      {/* AI SETUP FEE */}
      <section className="py-16 bg-brand-5 border-y border-gray-20">
        <div className="section-container max-w-3xl">
          <motion.div
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="text-center space-y-4"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-success-10 border border-success-20 rounded-full text-xs font-semibold text-success-60 uppercase tracking-wide">
              <Star className="w-3.5 h-3.5" />
              Early Access Offer
            </div>
            <h2 className="text-2xl font-bold text-gray-80">AI Setup &amp; Onboarding Fee</h2>
            <p className="text-3xl font-bold text-gray-80">$199 <span className="text-base font-normal text-gray-40">one-time</span></p>
            <p className="text-sm text-gray-50 leading-relaxed max-w-xl mx-auto">
              This includes help connecting your AI usage account, configuring your Session Intelligence<sup className="text-[65%] align-super">™</sup> workspace, and showing you how to generate your first session report.
            </p>
            <div className="inline-block bg-success-10 border border-success-20 rounded-2xl px-6 py-4">
              <p className="text-sm font-semibold text-success-60">
                Early Access Bonus — Join on a yearly plan during early access and the $199 AI Setup &amp; Onboarding Fee is waived.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PRICING NOTE */}
      <section className="py-12 bg-white">
        <div className="section-container max-w-3xl">
          <div className="space-y-3 text-sm text-gray-50 leading-relaxed">
            <p>Session Intelligence<sup className="text-[65%] align-super">™</sup> uses your own connected AI usage account so we can keep the monthly platform price lower and allow unlimited report generation.</p>
            <p>AI usage is billed separately by the AI provider and depends on how much you use the platform, session length, and report volume.</p>
            <p>No technical experience is required. We help you set this up during onboarding.</p>
          </div>
        </div>
      </section>

      {/* SAFETY NOTE */}
      <section className="py-10 bg-brand-5 border-t border-gray-20">
        <div className="section-container max-w-3xl">
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
            <p className="text-xs text-amber-700 leading-relaxed">
              <span className="font-semibold">Important: </span>
              Session Intelligence<sup className="text-[65%] align-super">™</sup> provides coaching and practitioner hypotheses, not diagnosis. It does not provide therapy, medical advice, legal advice, psychological assessment, or mental health assessment. You stay in control of the interpretation and should validate insights with your client using your own professional judgement.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="section-container max-w-2xl">
          <motion.h2
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="text-2xl font-bold text-gray-80 mb-10 text-center"
          >
            Frequently Asked Questions
          </motion.h2>
          <div className="space-y-2">
            {FAQS.map((faq, i) => (
              <motion.div
                key={i}
                variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i * 0.05}
                className="bg-brand-5 rounded-2xl overflow-hidden border border-gray-20"
              >
                <button
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="font-medium text-gray-80">{faq.q}</span>
                  {openFaq === i
                    ? <ChevronUp className="w-5 h-5 text-gray-40 flex-shrink-0" />
                    : <ChevronDown className="w-5 h-5 text-gray-40 flex-shrink-0" />}
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-6">
                    <p className="text-sm text-gray-50 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-10 bg-gray-80 border-t border-white/10">
        <div className="section-container flex flex-col md:flex-row items-center justify-between gap-4">
          <span className="text-gray-40 text-sm">Session Intelligence<sup className="text-[65%] align-super">™</sup></span>
          <div className="flex items-center gap-6 text-gray-40 text-sm">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
            <Link to="/disclaimer" className="hover:text-white transition-colors">Disclaimer</Link>
          </div>
          <p className="text-gray-50 text-xs">Copyright 2025 Session Intelligence<sup className="text-[65%] align-super">™</sup>, All Rights Reserved.</p>
        </div>
      </footer>
    </div>
  );
}
